<template>
    <figure :class="chartContainerClass">
        <div :class="chartContainerClassContent">
            <div class="chart--content" ref="chart"/>
        </div>
        <figcaption>
            <chart-legend :school-legend="legendItems" :type="type"/>
        </figcaption>
    </figure>
</template>

<script>
    import * as d3 from 'd3';
    import chartMixin from '../chart-mixin.js';
    import { MONOCHROME_DARK_GRAY, MONOCHROME_DARK_SILVER, MONOCHROME_MED_GRAY } from '../constants';
    import ChartLegend from '../chart-legend/chart-legend.vue';

    export default {
        components: {
            ChartLegend,
        },
        mixins: [chartMixin],
        props: {
            type: {
                type: String,
                default: '',
            },
            data: {
                type: [String, Array],
                default: () => [],
            },
        },
        data() {
            return {
                legendItems: [],
            };
        },
        methods: {
            renderVerticalBarChart() {
                const width = 720;
                const height = 350;
                const padding = 55;
                const barWidth = 32;
                const containerPadding = 10;

                const svg = this.setupSVG(width, height);

                const xScale = this.setupXScale(
                    this.chartData.map(d => d.name),
                    [padding + containerPadding, width - padding - containerPadding],
                );

                const xAxis = d3.axisBottom(xScale).tickSize(0)
                    .tickValues(xScale.domain().filter(d => !d.startsWith('placeholder')));

                svg.append('g')
                    .attr('class', 'chart--bar-vertical-x-axis')
                    .attr('transform', `translate(0,${ height - padding })`)
                    .call(xAxis)
                    .select('.domain')
                    .attr('stroke-width', '0');

                svg.selectAll('text')
                    .attr('transform', 'translate(0,17)')
                    .attr('fill', MONOCHROME_DARK_GRAY)
                    .attr('font-size', '12px')
                    .each(function() {
                        let text = d3.select(this);
                        let words = text.text().split(' ');
                        text.text('');
                        words.forEach((word, i) => {
                            const getDyValue = (index) => {
                                if (index === 0) { return '0'; };
                                if (index === words.length - 1) { return '15px'; };
                                return '10px';
                            };
                            text.append('tspan')
                                .attr('x', 0)
                                .attr('dy', getDyValue(i))
                                .text(word);
                        });
                    });

                const yScale = this.setupYScale([1, 6], [height - padding, padding]);

                const yAxisTicks = [1, 2, 3, 4, 5, 6];

                const yAxis = d3.axisLeft(yScale).tickSize(0)
                    .tickValues(yAxisTicks)
                    .tickFormat(d => `Wert ${ d }.0`);

                svg.append('g')
                    .attr('class', 'chart--bar-vertical-y-axis')
                    .attr('transform', `translate(${ padding },0)`)
                    .call(yAxis)
                    .selectAll('text')
                    .attr('class', 'chart--bar-vertical-y-axis-text')
                    .attr('font-size', '12px')
                    .attr('color', MONOCHROME_MED_GRAY)
                    .attr('transform', 'translate(-5,0)');

                svg.select('.chart--bar-vertical-y-axis .domain').remove();

                svg.selectAll('.chart--horizontal-grid-line')
                    .data(yAxisTicks)
                    .enter()
                    .append('line')
                    .attr('class', 'chart--horizontal-grid-line')
                    .attr('x1', padding)
                    .attr('y1', d => yScale(d) + 0.5)
                    .attr('x2', width - padding)
                    .attr('y2', d => yScale(d) + 0.5)
                    .attr('stroke', MONOCHROME_DARK_SILVER)
                    .attr('stroke-width', 1);

                svg.selectAll('.chart--bar-vertical')
                    .data(this.chartData)
                    .enter()
                    .append('rect')
                    .attr('class', 'chart--bar-vertical')
                    .attr('x', d => xScale(d.name) + (xScale.bandwidth() - barWidth) / 2)
                    .attr('y', d => yScale(d.value))
                    .attr('width', barWidth)
                    .attr('height', d => height - padding - yScale(d.value))
                    .attr('fill', d => d.color);

                this.chartData.filter(d => !d.name.startsWith('placeholder'))
                    .forEach((d) => {
                        const [beforeEquals, afterEquals] = d.description.split('=');
                        this.legendItems.push({ name: beforeEquals, value: afterEquals });
                    });
            },
        },
        mounted() {
            this.renderVerticalBarChart();
        },
    };
</script>
